import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Row, Col, DatePicker, Select, InputNumber, Switch, Typography, Divider, Space } from 'antd';
import { ArrowLeftOutlined, BankOutlined, EnvironmentOutlined, IdcardOutlined, UserOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { createAgency, editAgency, fetchAgencyTypes } from '../../store/superuserSlice';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const CreateAgencyPage = () => {
  const [form] = Form.useForm();
  const isActive = Form.useWatch('active', form);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const { agencyTypes, agencyTypesLoading } = useSelector((state) => state.superuser);

  useEffect(() => {
    dispatch(fetchAgencyTypes());
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && location.state) {
      const record = location.state;
      form.setFieldsValue({
        ...record,
        from_date: record.from_date ? dayjs(record.from_date) : null,
        to_date: record.to_date ? dayjs(record.to_date) : null,
        owner_first_name: record.owner?.first_name,
        owner_last_name: record.owner?.last_name,
        owner_email: record.owner?.email,
      });
    }
  }, [isEditMode, location.state, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        from_date: values.from_date ? values.from_date.format('YYYY-MM-DD') : null,
        to_date: values.to_date ? values.to_date.format('YYYY-MM-DD') : null,
      };

      let resultAction;
      if (isEditMode) {
        // Exclude owner_password if it wasn't modified or if backend doesn't want it, but typically we send what's in the form.
        resultAction = await dispatch(editAgency({ id, data: payload }));
      } else {
        resultAction = await dispatch(createAgency(payload));
      }

      if (editAgency.fulfilled.match(resultAction) || createAgency.fulfilled.match(resultAction)) {
        toast.success(`Agency ${isEditMode ? 'updated' : 'created'} successfully!`);
        navigate("/agencies");
      } else {
        toast.error(resultAction.payload || `Failed to ${isEditMode ? 'update' : 'create'} agency`);
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
    marginBottom: '32px',
    border: '1px solid #f0f0f0',
    padding: '16px'
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    color: '#f2654a'
  };

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100vh', padding: '0px 2px' }}>

      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Space size="middle" align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ fontSize: '18px', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
          />
          <Title level={3} style={{ margin: 0 }}>{isEditMode ? 'Edit Agency' : 'Create New Agency'}</Title>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ active: true }}
        requiredMark={false}
      >
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            {/* General Info Card */}
            <Card bordered={false} style={cardStyle}>
              <div style={sectionHeaderStyle}>
                <BankOutlined style={{ fontSize: '20px' }} />
                <Title level={5} style={{ margin: 0 }}>General Information</Title>
              </div>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Company Name</Text>} name="company_name" rules={[{ required: true, message: 'Please enter company name' }]}>
                    <Input placeholder="Skyline Recovery Services" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Agency Type</Text>} name="agency_type" rules={[{ required: true, message: 'Please select agency type' }]}>
                    <Select placeholder="Select Agency Type" size="large" loading={agencyTypesLoading}>
                      {Array.isArray(agencyTypes) && agencyTypes.map(type => (
                        <Select.Option key={type.id || type.agency_type_id} value={type.id || type.agency_type_id}>
                          {type?.agency_type}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Line of Business</Text>} name="line_of_business">
                    <Input placeholder="Debt Recovery" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Functional Area</Text>} name="functional_area">
                    <Input placeholder="Collections" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Contract From</Text>} name="from_date">
                    <DatePicker style={{ width: '100%', borderRadius: '6px' }} size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Contract To</Text>} name="to_date">
                    <DatePicker style={{ width: '100%', borderRadius: '6px' }} size="large" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Location & Contact Card */}
            <Card bordered={false} style={cardStyle}>
              <div style={sectionHeaderStyle}>
                <EnvironmentOutlined style={{ fontSize: '20px' }} />
                <Title level={5} style={{ margin: 0 }}>Location & Contact</Title>
              </div>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Telephone Number</Text>} name="telephone_number">
                    <Input placeholder="07940001234" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Registered Address</Text>} name="registered_address">
                    <Input placeholder="402, Iscon Tower, SG Highway" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label={<Text strong>Registered City</Text>} name="registered_city">
                    <Input placeholder="Ahmedabad" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label={<Text strong>Operating City</Text>} name="city">
                    <Input placeholder="Ahmedabad" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label={<Text strong>Pin Code</Text>} name="pin_code">
                    <Input placeholder="380015" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label={<Text strong>Presence in States (Comma separated)</Text>} name="presence_in_states">
                    <Input placeholder="Gujarat, Maharashtra" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label={<Text strong>Operating Cities (Comma separated)</Text>} name="cities">
                    <Input placeholder="Ahmedabad, Surat, Mumbai" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            {/* Status & Limits Card */}
            <Card bordered={false} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Title level={5} style={{ margin: 0, color: '#f2654a' }}>Agency Status</Title>
                <Form.Item name="active" valuePropName="checked" style={{ margin: 0 }}>
                  <Switch style={{ backgroundColor: (isActive !== false) ? '#f2654a' : undefined }} />
                </Form.Item>
              </div>

              <Form.Item label={<Text strong>Agent Limit</Text>} name="agent_limit" rules={[{ required: true, message: 'Required' }]}>
                <InputNumber style={{ width: '100%', borderRadius: '6px' }} size="large" min={1} placeholder="7" />
              </Form.Item>
            </Card>

            {/* Owner Details Card */}
            <Card bordered={false} style={cardStyle}>
              <div style={sectionHeaderStyle}>
                <UserOutlined style={{ fontSize: '20px' }} />
                <Title level={5} style={{ margin: 0 }}>Owner Profile</Title>
              </div>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>First Name</Text>} name="owner_first_name" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="Rahul" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={<Text strong>Last Name</Text>} name="owner_last_name" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="Mehta" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label={<Text strong>Email Address</Text>} name="owner_email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
                    <Input placeholder="owner@skylinerecovery.com" size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label={<Text strong>Password</Text>} name="owner_password" rules={[{ required: !isEditMode, message: 'Required' }]}>
                    <Input.Password placeholder={isEditMode ? 'Leave blank to keep unchanged' : 'StrongPass@123'} size="large" style={{ borderRadius: '6px' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Compliance Card */}
            <Card bordered={false} style={cardStyle}>
              <div style={sectionHeaderStyle}>
                <IdcardOutlined style={{ fontSize: '20px' }} />
                <Title level={5} style={{ margin: 0 }}>Compliance Details</Title>
              </div>

              <Form.Item label={<Text strong>PAN No</Text>} name="pan_no">
                <Input placeholder="AABCS1234K" size="large" style={{ borderRadius: '6px', textTransform: 'uppercase' }} />
              </Form.Item>
              <Form.Item label={<Text strong>Service Tax Number</Text>} name="service_tax_number">
                <Input placeholder="24AABCS1234K1ZP" size="large" style={{ borderRadius: '6px', textTransform: 'uppercase' }} />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <Space>
            <Button size="large" onClick={() => navigate(-1)} style={{ borderRadius: '6px', fontWeight: 500 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              loading={loading}
              style={{ backgroundColor: '#f2654a', borderColor: '#f2654a', borderRadius: '6px', fontWeight: 500, padding: '0 24px' }}
            >
              {isEditMode ? 'Save Changes' : 'Create Agency'}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default CreateAgencyPage;
